import React, { useCallback, useContext, useState } from 'react';

import { check } from '@/assets/icons';
import { SetlistMetadata } from '@/components/ChatContainer/components/ChatList/components/RenderMessage/components/MessageSetlist';
import { LayoutContext } from '@/components/Layout';
import { SetlistSummaryCard } from '@/components/SetlistSummaryCard';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { setlistsUrl } from '@/config/urls';
import { MessageType, useSendChatMessageMutation } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useSetlists } from '@/providers/SetlistsProvider';
import { SvgIcon } from '@material-ui/core';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import styles from './ShareDialog.module.scss';

type ShareDialogProps = {
	onClose: () => void;
	chatId: string;
};

const ShareDialog: React.FC<ShareDialogProps> = ({ onClose, chatId }) => {
	const navigate = useNavigate();

	const { setlists, refetch } = useSetlists();
	const { handleError } = useErrorHandler();

	const { mutateAsync: sendChatMessage } = useSendChatMessageMutation();

	const [selectedSetlistId, setSelectedSetlistId] = useState<string>();

	const sendMessage = useCallback(async () => {
		try {
			if (!selectedSetlistId) {
				throw new Error('No setlist selected');
			}
			const id = uuidv4();
			const metadata: SetlistMetadata = {
				setlistId: selectedSetlistId,
				songsLength:
					setlists.find((s) => s.id === selectedSetlistId)
						?.teamSongIds.length || 1,
			};
			await sendChatMessage({
				chatId,
				input: {
					id,
					content: 'Shared setlist',
					type: MessageType.Setlist,
					metadata: JSON.stringify(metadata),
				},
			});
			refetch();
			onClose();
		} catch (error) {
			handleError({
				errorCode: 'ShareSetlistError',
				error,
				friendlyMessage: `There was an issue with sharing this setlist. Please try again.`,
			});
		}
	}, [
		selectedSetlistId,
		setlists,
		sendChatMessage,
		chatId,
		refetch,
		onClose,
		handleError,
	]);

	const noSetlists = !setlists.length;

	return (
		<AsaphDialog
			className={styles['root']}
			open={true}
			onClose={() => onClose()}
		>
			<div className={styles['toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: (
							<AsaphDialogHeaderButton
								type="text"
								label="Cancel"
								handlePress={() => onClose()}
							/>
						),
					}}
					title={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: 'Share setlist',
					}}
					trailingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: '',
					}}
				/>
			</div>
			<div className={styles['toolbar-divider']}>
				<hr />
			</div>
			<section className={styles['content']}>
				<BottomActionLayout
					contentClassName={styles['full-height']}
					actionContent={
						<div>
							<AsaphButton
								fullWidth
								disabled={!selectedSetlistId}
								color="blue"
								label="Share setlist"
								onClick={sendMessage}
							/>
						</div>
					}
				>
					{!noSetlists ? (
						setlists.map((setlist, si) => {
							const checked = selectedSetlistId === setlist.id;
							return (
								<div
									className={classNames([
										si === setlists.length - 1 &&
											styles['last'],
										styles['wrapper'],
									])}
									onClick={() =>
										setSelectedSetlistId(setlist.id)
									}
								>
									<SetlistSummaryCard
										size="md"
										title="Test"
										setlistId={setlist.id}
										setlist={setlist}
										timeClassName={
											styles['setlist-summary-time']
										}
										className={
											styles['setlist-summary-card']
										}
									/>
									<div className={styles['selectable']}>
										<div
											className={classNames([
												styles['card-icon-container'],
												checked && styles['checked'],
											])}
										>
											{checked && (
												<SvgIcon
													component={check}
													classes={{
														root: styles[
															'card-icon'
														],
													}}
												/>
											)}
										</div>
									</div>
								</div>
							);
						})
					) : (
						<div className={styles['empty-message-wrapper']}>
							<EmptyResourceMessage
								title="There are no setlists yet"
								subtitle="Create a setlist from the Setlist page"
								actionLabel="Go to Setlists"
								onActionClick={() => navigate(setlistsUrl)}
							/>
						</div>
					)}
				</BottomActionLayout>
			</section>
		</AsaphDialog>
	);
};

export default ShareDialog;
